import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';
import { getTenantFromUrl } from '../utils/enhancedTenantUtils';

const ForgotPassword: React.FC = () => {
  const tenantInfo = getTenantFromUrl();
  // Match backend passwordResetPolicy: Stemmler/default surface only.
  const allowSelfServiceReset = tenantInfo.isMainDomain || tenantInfo.isDefault;

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success('Password reset instructions sent to your email');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-super-dark-purple to-dusky-purple z-0"></div>

      <div className="max-w-md w-full space-y-8 relative z-20">
        <div className="text-center">
          <div className="w-40 h-28 mx-auto mb-4 flex items-center justify-center">
            <img
              src="/images/jarvis_logo.png"
              alt="Jarvis Logo"
              className="h-28 w-40 object-contain"
              style={{ display: 'block' }}
            />
          </div>
          <h2 className="text-3xl font-bold text-white">JARVIS RCSA Platform</h2>
          <p className="mt-2 text-gray-300">AI-Powered Risk & Control Assessment</p>
        </div>

        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-2xl p-8 border border-white border-opacity-20">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {allowSelfServiceReset ? 'Forgot Password' : 'Password Reset'}
            </h1>
            <p className="text-gray-600 text-sm">
              {allowSelfServiceReset
                ? 'Enter your email to receive a password reset link'
                : 'Self-service reset is not available for this organization'}
            </p>
          </div>

          {!allowSelfServiceReset ? (
            <div className="text-center space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3 text-left">
                  <Mail className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-900 text-sm">
                    To reset your password, please contact your organization&apos;s administrator.
                    They can issue a temporary password from the Admin user management screen.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-dusky-purple hover:text-darker-blue transition-colors"
                >
                  <ArrowLeft size={16} className="mr-1" />
                  Back to Login
                </Link>
              </div>
            </div>
          ) : !submitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:border-electric-blue"
                  placeholder="your.email@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-electric-blue hover:bg-darker-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-electric-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-dusky-purple hover:text-darker-blue transition-colors"
                >
                  <ArrowLeft size={16} className="mr-1" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 text-sm">
                  If your email exists in our system, you will receive a password reset link shortly.
                  Please check your inbox and spam folder.
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-gray-600">Didn&apos;t receive the email?</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-dusky-purple hover:text-darker-blue text-sm font-medium"
                >
                  Try again
                </button>
              </div>

              <div className="pt-4">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-dusky-purple hover:text-darker-blue transition-colors"
                >
                  <ArrowLeft size={16} className="mr-1" />
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
